import React, { useState } from 'react';
import { authFetch } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

function formatCurrency(val) {
  return `£${Number(val).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`;
}

const STATUS_CONFIG = {
  initiated:          { label: 'Initiated',  dot: 'bg-tf-warning',  border: 'border-tf-warning' },
  delivery_confirmed: { label: 'Confirmed',  dot: 'bg-blue-500',   border: 'border-blue-500' },
  funds_released:     { label: 'Released',   dot: 'bg-tf-success',  border: 'border-tf-success' },
  disputed:           { label: 'Disputed',   dot: 'bg-tf-danger',   border: 'border-tf-danger' },
  resolved:           { label: 'Resolved',   dot: 'bg-tf-success',  border: 'border-tf-success' },
  cancelled:          { label: 'Cancelled',  dot: 'bg-tf-text-3',   border: 'border-tf-text-3' },
};

const NEXT_STATUSES = {
  initiated: ['delivery_confirmed', 'disputed', 'cancelled'],
  delivery_confirmed: ['funds_released', 'disputed'],
  disputed: ['resolved', 'cancelled'],
};

export default function TransactionsTable({ transactions, onRefresh }) {
  const [updating, setUpdating] = useState(null);

  const updateStatus = async (txnId, newStatus) => {
    setUpdating(txnId);
    try {
      const res = await authFetch(`${API}/api/transactions/update-status`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ txn_id: txnId, new_status: newStatus }),
      });
      if (res.ok && onRefresh) onRefresh();
    } catch (err) { console.error('Update failed:', err); }
    finally { setUpdating(null); }
  };

  const downloadCert = async (txnId) => {
    try {
      await authFetch(`${API}/api/certificates/generate/${txnId}`, { method: 'POST' });
      const res = await authFetch(`${API}/api/certificates/${txnId}`);
      if (!res.ok) return;
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url;
      a.download = `TrustFrame_Certificate_${txnId}.pdf`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch (err) { console.error('Certificate download failed:', err); }
  };

  return (
    <div className="bg-tf-surface border border-tf-primary" data-testid="transactions-table">
      <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-tf-primary flex items-center justify-between">
        <h2 className="font-heading font-bold text-xs sm:text-sm tracking-[0.15em] sm:tracking-[0.2em] uppercase text-tf-text">Escrow Transactions</h2>
        <span className="font-mono text-[8px] sm:text-[9px] tracking-[0.2em] uppercase text-tf-text-2">{transactions.length} total</span>
      </div>
      {transactions.length === 0 ? (
        <div className="px-4 sm:px-5 py-8 sm:py-12 text-center">
          <p className="font-body text-sm text-tf-text-2">No transactions yet.</p>
        </div>
      ) : (
        <>
          {/* Mobile cards */}
          <div className="sm:hidden">
            {transactions.map((txn, i) => {
              const st = txn.status || 'initiated';
              const config = STATUS_CONFIG[st] || STATUS_CONFIG.initiated;
              const nextStatuses = NEXT_STATUSES[st] || [];
              return (
                <div key={txn.txn_id || i} className="px-4 py-3 border-b border-tf-border-muted last:border-b-0" data-testid={`txn-row-${i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono text-[9px] text-tf-text-2">{txn.txn_id}</span>
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 border ${config.border} font-mono text-[8px] tracking-[0.1em] uppercase`}>
                      <div className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />{config.label}
                    </span>
                  </div>
                  <p className="font-body text-sm text-tf-text truncate">{txn.description}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="font-heading font-bold text-sm text-tf-text">{formatCurrency(txn.amount)}</span>
                    <span className="font-mono text-[9px] text-tf-text-2">T{txn.tier}</span>
                  </div>
                  {(nextStatuses.length > 0 || st === 'funds_released') && (
                    <div className="flex flex-wrap gap-1 mt-2 pt-2 border-t border-tf-border-muted">
                      {nextStatuses.map(ns => {
                        const nsC = STATUS_CONFIG[ns];
                        return <button key={ns} data-testid={`txn-action-${ns}-${i}`} onClick={() => updateStatus(txn.txn_id, ns)} disabled={updating === txn.txn_id}
                          className={`px-2 py-0.5 border ${nsC.border} font-mono text-[8px] tracking-[0.1em] uppercase disabled:opacity-30`}>{nsC.label}</button>;
                      })}
                      {st === 'funds_released' && (
                        <button data-testid={`cert-download-${i}`} onClick={() => downloadCert(txn.txn_id)}
                          className="px-2 py-0.5 border border-tf-accent text-tf-accent font-mono text-[8px] tracking-[0.1em] uppercase">Certificate</button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Desktop table */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className="border-b border-tf-border-muted">
                  {['ID', 'Artwork', 'Value', 'Tier', 'Status', 'Actions'].map(h => (
                    <th key={h} className="px-4 sm:px-5 py-2 sm:py-3 text-left font-mono text-[8px] sm:text-[9px] tracking-[0.2em] uppercase text-tf-text-2">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {transactions.map((txn, i) => {
                  const st = txn.status || 'initiated';
                  const config = STATUS_CONFIG[st] || STATUS_CONFIG.initiated;
                  const nextStatuses = NEXT_STATUSES[st] || [];
                  return (
                    <tr key={txn.txn_id || i} className="border-b border-tf-border-muted last:border-b-0 hover:bg-tf-bg transition-colors duration-200" data-testid={`txn-row-${i}`}>
                      <td className="px-4 sm:px-5 py-2 sm:py-3 font-mono text-[9px] sm:text-[10px] text-tf-text-2">{txn.txn_id}</td>
                      <td className="px-4 sm:px-5 py-2 sm:py-3 font-body text-xs sm:text-sm text-tf-text max-w-[140px] sm:max-w-[180px] truncate">{txn.description}</td>
                      <td className="px-4 sm:px-5 py-2 sm:py-3 font-heading font-bold text-xs sm:text-sm text-tf-text">{formatCurrency(txn.amount)}</td>
                      <td className="px-4 sm:px-5 py-2 sm:py-3 font-mono text-[10px] text-tf-text-2">T{txn.tier}</td>
                      <td className="px-4 sm:px-5 py-2 sm:py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 border ${config.border} font-mono text-[8px] sm:text-[9px] tracking-[0.1em] uppercase`}>
                          <div className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />{config.label}
                        </span>
                      </td>
                      <td className="px-4 sm:px-5 py-2 sm:py-3">
                        {nextStatuses.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {nextStatuses.map(ns => {
                              const nsC = STATUS_CONFIG[ns];
                              return <button key={ns} data-testid={`txn-action-${ns}-${i}`} onClick={() => updateStatus(txn.txn_id, ns)} disabled={updating === txn.txn_id}
                                className={`px-2 py-0.5 border ${nsC.border} font-mono text-[7px] sm:text-[8px] tracking-[0.1em] uppercase hover:opacity-70 disabled:opacity-30`}>{nsC.label}</button>;
                            })}
                          </div>
                        ) : <span className="font-mono text-[10px] text-tf-text-3">-</span>}
                        {st === 'funds_released' && (
                          <button data-testid={`cert-download-${i}`} onClick={() => downloadCert(txn.txn_id)}
                            className="mt-1 px-2 py-0.5 border border-tf-accent text-tf-accent font-mono text-[7px] sm:text-[8px] tracking-[0.1em] uppercase hover:bg-tf-accent hover:text-white transition-all">Certificate</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
